import  { useRef, useState } from "react";
import { FaUser } from "react-icons/fa";
import { IoCloseOutline } from "react-icons/io5";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosServicesOnboarding from "../../../services/axiosServicesOnboarding";
import { Box, Typography } from "@mui/material";

const StepBox = ({ number, title }) => {
  return (
    <Box position="relative">
      <Box
        display="flex"
        alignItems="center"
        border="1px solid #e0e0e0"
        borderRadius="8px"
        padding="6px"
        bgcolor="#ffffff"
        position="relative"
      >
        {/* Number Circle */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="40px"
          height="40px"
          border="2px solid #001d7e"
          borderRadius="50%"
          marginRight="8px"
        >
          <Typography variant="body1" color="#001d7e" fontWeight="bold">
            {number}
          </Typography>
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight="bold" color="#001d7e">
          {title}
        </Typography>

        {/* Pointed edge - border */}
        <Box
          position="absolute"
          right="-21px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "21px solid #e0e0e0",
            borderTop: "21px solid transparent",
            borderBottom: "21px solid transparent",
          }}
        />

        {/* Pointed edge - fill */}
        <Box
          position="absolute"
          right="-20px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "20px solid #ffffff",
            borderTop: "20px solid transparent",
            borderBottom: "20px solid transparent",
            zIndex: 1,
            height: "60px",
          }}
        />
      </Box>
    </Box>
  );
};

const NinVerification = ({ onBack, data, refresh, setRefresh }) => {
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const uniqueNumbersRef = useRef(null);

  const generateUniqueNumbers = () => {
    if (!uniqueNumbersRef.current) {
      const uniqueSet = new Set();
      while (uniqueSet.size < 10) {
        const timestamp = Date.now();
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        uniqueSet.add(`${timestamp}${randomNum}`);
      }
      uniqueNumbersRef.current = Array.from(uniqueSet);
    }
    return uniqueNumbersRef.current;
  };

  const validationSchema = Yup.object({
    NIN: Yup.string()
      .matches(/^[0-9]{10,15}$/, "Invalid NIN number")
      .required("NIN number is required"),
    otp: otpSent
      ? Yup.string()
          .required("OTP is required")
          .length(6, "OTP must be exactly 6 digits")
      : Yup.string(),
  });

  const handleApiCall = async (values) => {
    const uniqueNumbers = generateUniqueNumbers();

    const body = {
      nin: values.NIN,
    };

    try {
      setLoading(true);
      setError("");

      const res = await axiosServicesOnboarding.post(
        "Customer/verify-nin",
        body
      );
      console.log("NIN verification response:", res);

      setRefresh(!refresh); // Refresh the parent component
      onBack(); // Close the current view
      toast.success("NIN verified successfully!");
    } catch (error) {
      console.error("API error:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");
      setError(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full md:w-[650px] mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2 text-gray-700 text-lg font-semibold">
          <FaUser className="text-xl text-primary-color" />
          <span>Verify NIN Number</span>
        </div>
        <button onClick={onBack} className="text-gray-600 text-2xl">
          {/* <IoCloseOutline /> */}
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.0003 29.3337C23.3641 29.3337 29.3337 23.3641 29.3337 16.0003C29.3337 8.63653 23.3641 2.66699 16.0003 2.66699C8.63653 2.66699 2.66699 8.63653 2.66699 16.0003C2.66699 23.3641 8.63653 29.3337 16.0003 29.3337Z"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 12L12 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 12L20 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="px-8 py-1 bg-[#edf1f9] rounded-md mb-8">
        <Box maxWidth="600px" width="100%" p={2}>
          <StepBox number="01" title="Verify NIN Number" />
        </Box>
      </div>

      <h3 className="text-lg font-semibold text-primary-color text-center mb-6">
        NIN Verification
      </h3>

      <Formik
        initialValues={{ NIN: "", otp: "" }}
        validationSchema={validationSchema}
        onSubmit={handleApiCall}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-6 w-full md:w-[400px] mx-auto">
            <div>
              <label className="block text-gray-600 font-medium mb-2">
                NIN Number <span className="text-red-500">*</span>
              </label>
              <Field
                type="text"
                name="NIN"
                placeholder="Enter your NIN number"
                className="w-full p-3 border border-gray-300 rounded-md"
                disabled={otpSent}
              />
              <ErrorMessage
                name="NIN"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {otpSent && (
              <div>
                <label className="block text-gray-600 font-medium mb-2">
                  Enter OTP:
                </label>
                <Field
                  type="text"
                  name="otp"
                  placeholder="Enter OTP"
                  className="w-full p-3 border border-gray-300 rounded-md"
                />
                <ErrorMessage
                  name="otp"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>
            )}

            <div className="text-[13px] text-[#171832] text-center w-full mx-auto flex items-center justify-center">
              <p className="w-[285px] text-[#171832]">
                We will check if this NIN matches personal information. An OTP
                will be sent to your phone number
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-color hover:hover:bg-[#8388B6] text-white font-semibold py-4 px-6 rounded-md"
            >
              {loading ? "Loading..." : otpSent ? "Verify" : "Verify"}
            </button>

            {otpSent && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Didn’t get OTP?{" "}
                <span
                  className="text-primary-color cursor-pointer"
                  onClick={() => setOtpSent(false)}
                >
                  Resend
                </span>
              </p>
            )}

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default NinVerification;
